import { createSlice } from "@reduxjs/toolkit";
import APIService from "../services/api.service";
import { AppDispatch } from "../store";

const initialState = {
  documentDialogOpen: false,
  documentDrawerOpen: false,
  documentIsLoading: false,
  documentStatus: null,
  documentNextAction: null,
  documents: [],
  documentsLoading: false,
  // String-keyed concerning keys picked in the shared tree, same shape as the
  // unknown slice's selection.
  selectedConcernings: []
};

export const document = createSlice({
  name: "document",
  initialState,
  reducers: {
    openDocumentDialog: (state, action) => {
      state.documentDialogOpen = true;
      state.selectedConcernings = action.payload?.element?.concernings
        ? action.payload.element.concernings.map((c) => (typeof c === 'object' && c !== null ? c.value || c.id || c : c))
        : [];
    },
    closeDocumentDialog: (state) => {
      state.documentDialogOpen = false;
    },
    openDocumentDrawer: (state) => {
      state.documentDrawerOpen = true;
    },
    closeDocumentDrawer: (state) => {
      state.documentDrawerOpen = false;
    },
    loadingDocument: (state) => {
      state.documentIsLoading = true;
    },
    resetDocument: (state, action) => {
      state.documentIsLoading = false;
      state.documentStatus = 'success';
      state.documentNextAction = action.payload;
    },
    resetDocumentStatus: (state) => {
      state.documentStatus = null;
    },
    setDocuments: (state, action) => {
      state.documents = action.payload ? action.payload : [];
      state.documentsLoading = false;
    },
    loadingDocuments: (state) => {
      state.documentsLoading = true;
    },
    addSelectedDocumentConcerning: (state, action) => {
      if (!state.selectedConcernings.includes(action.payload)) {
        state.selectedConcernings.push(action.payload);
      }
    },
    removeSelectedDocumentConcerning: (state, action) => {
      state.selectedConcernings = state.selectedConcernings.filter((id) => id !== action.payload);
    },
    clearSelectedDocumentConcernings: (state) => {
      state.selectedConcernings = [];
    }
  },
});

export const {
  openDocumentDialog,
  closeDocumentDialog,
  openDocumentDrawer,
  closeDocumentDrawer,
  loadingDocument,
  resetDocument,
  resetDocumentStatus,
  setDocuments,
  loadingDocuments,
  addSelectedDocumentConcerning,
  removeSelectedDocumentConcerning,
  clearSelectedDocumentConcernings
} = document.actions;

export default document.reducer;

// Upload a document. `file` is optional — a document can instead carry a `url`
// (the external-link case); the URL is just another key in `data`.
export const documentPost = (nextAction, data: any, file = null) => async (dispatch: AppDispatch) => {
  try {
    dispatch(loadingDocument());
    const form = new FormData();
    if (file) {
      form.append("file", file);
    }
    Object.keys(data || {}).forEach((key) => {
      if (data[key] !== undefined && data[key] !== null) {
        form.append(key, data[key]);
      }
    });
    await APIService.post("/documents", form, {
      headers: { "Content-Type": "multipart/form-data" }
    });
    dispatch(resetDocument(nextAction));
    dispatch(documentsGetAll());
  } catch (e) {
    dispatch(resetDocumentStatus());
  }
};

// Soft-delete a document (the backend's DELETE /documents/:id deactivates it),
// then refresh the list so it disappears from both the drawer and the Reports
// table (both read state.document.documents).
export const documentDelete = (id: string) => async (dispatch: AppDispatch) => {
  try {
    await APIService.delete('/documents/' + id);
    dispatch(documentsGetAll());
  } catch (e) {
    // Keep the document visible on error (no refresh); mirror the slice's
    // existing best-effort error handling rather than corrupting the list.
    dispatch(resetDocumentStatus());
  }
};

// Loads the active project's documents (files + links) for the Documents list.
export const documentsGetAll = () => async (dispatch: AppDispatch) => {
  try {
    dispatch(loadingDocuments());
    const { data } = await APIService.get("/documents");
    dispatch(setDocuments(data.items || []));
  } catch (e) {
    dispatch(setDocuments([]));
  }
};
